'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Home, MapPin, Plane, Palette, Wine, Watch, Gem, Users, Crown, Building2, Car, Anchor, Music, EyeOff, HelpCircle, Archive, User2, Newspaper, Lock, BookOpen, Search, Menu, X, Library, LayoutDashboard, LogIn, LogOut, UserCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface AppLayoutProps {
  children: React.ReactNode;
  currentPath?: string;
}

const mainNav = [
  { label: 'Home', subtitle: "Today\'s discoveries & highlights", href: '/editorial-discovery-feed', icon: Home },
  { label: 'Extraordinary Places', subtitle: 'Extraordinary hotels, islands, villas & more', href: '/category?cat=extraordinary-places', icon: MapPin },
  { label: 'Luxury Travel', subtitle: 'Unusual experiences & journeys', href: '/category?cat=luxury-travel', icon: Plane },
  { label: 'Art & Collecting', subtitle: 'Rare art, private collections, auctions', href: '/category?cat=art-collecting', icon: Palette },
  { label: 'Whisky & Spirits', subtitle: 'Rare collections, distilleries & more', href: '/category?cat=whisky-spirits', icon: Wine },
  { label: 'Watches & Horology', subtitle: 'Iconic watches, rare collections', href: '/category?cat=watches-horology', icon: Watch },
  { label: 'Jewellery & Gems', subtitle: 'Extraordinary jewels & houses', href: '/category?cat=jewellery-gems', icon: Gem },
  { label: 'Private Clubs', subtitle: "The world\'s most exclusive clubs", href: '/category?cat=private-clubs', icon: Users },
  { label: 'Royalty & Dynasties', subtitle: 'Royal families, palaces & legacies', href: '/category?cat=royalty-dynasties', icon: Crown },
  { label: 'Architecture & Design', subtitle: 'Iconic buildings & interior worlds', href: '/category?cat=architecture-design', icon: Building2 },
  { label: 'Automobiles', subtitle: 'Rare cars, garages & legacies', href: '/category?cat=automobiles', icon: Car },
  { label: 'Aviation & Yachts', subtitle: 'Private jets, yachts & experiences', href: '/category?cat=aviation-yachts', icon: Anchor },
  { label: 'Lifestyle & Culture', subtitle: 'Rituals, objects & refined living', href: '/category?cat=lifestyle-culture', icon: Music },
  { label: 'Hidden Gems', subtitle: 'Places & experiences off the map', href: '/category?cat=hidden-gems', icon: EyeOff },
  { label: 'Who Owns It?', subtitle: 'Ownership behind iconic things', href: '/category?cat=who-owns-it', icon: HelpCircle },
  { label: 'The Collections', subtitle: "The world\'s most incredible collections", href: '/category?cat=the-collections', icon: Archive },
  { label: 'The People', subtitle: 'Visionaries, creators & connoisseurs', href: '/category?cat=the-people', icon: User2 },
  { label: 'News & Insights', subtitle: "What\'s happening in luxury", href: '/intelligence-desk', icon: Newspaper },
  { label: 'Private Desk', subtitle: 'Ask. Discover. Uncover.', href: '/category?cat=private-desk', icon: Lock },
  { label: 'My Library', subtitle: 'Your saved articles & collections', href: '/my-library', icon: Library },
  { label: 'My Profile', subtitle: 'Account, settings & reading history', href: '/profile', icon: UserCircle },
  { label: 'The Private Edition', subtitle: 'Weekly curated editions', href: '/category?cat=private-edition', icon: BookOpen },
];

interface NavItemProps {
  item: typeof mainNav[0];
  activePath: string;
  onClose: () => void;
}

function NavItem({ item, activePath, onClose }: NavItemProps) {
  const IconComp = item.icon;
  const base = item.href.split('?')[0];
  const active =
    (base === '/editorial-discovery-feed' && activePath === '/editorial-discovery-feed') ||
    (base !== '/editorial-discovery-feed' && activePath.startsWith(base) && base !== '/');

  return (
    <Link
      href={item.href}
      className={`flex items-start gap-2.5 px-3 py-1.5 rounded transition-all group ${active ? 'bg-[var(--secondary)]' : 'hover:bg-[var(--secondary)]'}`}
      onClick={onClose}
    >
      <IconComp size={14} strokeWidth={1.5} className={`mt-0.5 flex-shrink-0 ${active ? 'text-[var(--gold)]' : 'text-[var(--slate-muted)] group-hover:text-[var(--gold)]'}`} />
      <div className="min-w-0">
        <p className={`text-[12px] font-medium leading-tight ${active ? 'text-[var(--gold)]' : 'text-[var(--foreground)] group-hover:text-[var(--gold)]'} transition-colors`}>{item.label}</p>
        <p className="text-[10px] text-[var(--slate-muted)] leading-tight mt-0.5 line-clamp-1">{item.subtitle}</p>
      </div>
    </Link>
  );
}

interface MemberCtaProps {
  user: ReturnType<typeof useAuth>['user'];
  isSubscribed: ReturnType<typeof useAuth>['isSubscribed'];
  profile: ReturnType<typeof useAuth>['profile'];
  onClose: () => void;
  onSignOut: () => void;
}

function MemberCta({ user, isSubscribed, profile, onClose, onSignOut }: MemberCtaProps) {
  if (!user) {
    return (
      <div>
        <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[var(--gold)] text-center">PRIVATE MEMBER</p>
        <p className="text-[11px] font-semibold text-center text-[var(--foreground)] mt-1">₹1,899 / YEAR</p>
        <p className="text-[8px] tracking-[0.1em] uppercase text-[var(--slate-muted)] text-center mt-1">UNLOCK A WORLD BEYOND</p>
        <p className="text-[8px] tracking-[0.1em] uppercase text-[var(--slate-muted)]">THE ORDINARY</p>
        <Link
          href="/sign-up-login-screen"
          onClick={onClose}
          className="w-full mt-2.5 py-1.5 text-[10px] font-bold tracking-[0.12em] uppercase bg-[var(--gold)] text-white rounded transition-all hover:bg-[var(--gold-dark)] flex items-center justify-center gap-1"
        >
          <LogIn size={10} />
          BECOME A MEMBER
        </Link>
      </div>
    );
  }

  return (
    <div>
      <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[var(--gold)] text-center">
        {isSubscribed() ? 'ACTIVE MEMBER' : 'FREE ACCOUNT'}
      </p>
      <p className="text-[11px] font-semibold text-center text-[var(--foreground)] mt-1 truncate">
        {profile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0]}
      </p>
      <Link
        href="/subscriber-dashboard"
        onClick={onClose}
        className="w-full mt-2 py-1.5 text-[10px] font-bold tracking-[0.12em] uppercase bg-[var(--gold)] text-white rounded transition-all hover:bg-[var(--gold-dark)] flex items-center justify-center gap-1"
      >
        <LayoutDashboard size={10} />
        MY DASHBOARD
      </Link>
      <button
        onClick={onSignOut}
        className="w-full mt-1.5 py-1 text-[9px] font-semibold tracking-[0.1em] uppercase text-[var(--slate-muted)] hover:text-red-400 transition-colors flex items-center justify-center gap-1"
      >
        <LogOut size={9} />
        SIGN OUT
      </button>
    </div>
  );
}

interface SidebarContentProps {
  activePath: string;
  currentYear: number;
  user: ReturnType<typeof useAuth>['user'];
  isSubscribed: ReturnType<typeof useAuth>['isSubscribed'];
  profile: ReturnType<typeof useAuth>['profile'];
  onClose: () => void;
  onSignOut: () => void;
}

function SidebarContent({ activePath, currentYear, user, isSubscribed, profile, onClose, onSignOut }: SidebarContentProps) {
  return (
    <div suppressHydrationWarning className="flex flex-col h-full">
      <div suppressHydrationWarning className="flex-1 overflow-y-auto scroll-hidden">
        <div suppressHydrationWarning className="px-4 py-4 border-b border-[var(--border)]">
          <Link href="/editorial-discovery-feed" className="block">
            <p className="text-[9px] font-semibold tracking-[0.18em] uppercase text-[var(--slate-muted)] leading-none mb-0.5">THE PRIVATE WORLD OF</p>
            <p className="font-editorial text-[22px] font-bold text-[var(--gold)] leading-none tracking-wide">LUXURY</p>
            <p className="text-[8px] tracking-[0.15em] uppercase text-[var(--slate-muted)] mt-1">A PRIVATE WORLD OF</p>
            <p className="text-[8px] tracking-[0.15em] uppercase text-[var(--slate-muted)]">EXTRAORDINARY THINGS</p>
          </Link>
        </div>

        <div suppressHydrationWarning className="px-3 pt-3 pb-1">
          <p className="text-[9px] font-bold tracking-[0.18em] uppercase text-[var(--slate-muted)]">MAIN MENU</p>
        </div>

        <nav suppressHydrationWarning className="px-1 pb-2 flex flex-col gap-0">
          {mainNav.map((item) => (
            <NavItem key={item.href + item.label} item={item} activePath={activePath} onClose={onClose} />
          ))}
        </nav>

        <div suppressHydrationWarning className="mx-3 mt-2 mb-4 p-3 border border-[var(--gold)] rounded bg-[var(--secondary)]">
          <div className="flex justify-center mb-1.5">
            <Crown size={14} className="text-[var(--gold)]" />
          </div>
          <MemberCta
            user={user}
            isSubscribed={isSubscribed}
            profile={profile}
            onClose={onClose}
            onSignOut={onSignOut}
          />
        </div>
      </div>

      <div suppressHydrationWarning className="flex-shrink-0 border-t border-[var(--border)] px-3 py-3 bg-white flex flex-wrap gap-x-3 gap-y-1">
        <Link href="/privacy-policy" onClick={onClose} className="text-[9px] tracking-[0.1em] uppercase text-[var(--slate-muted)] hover:text-[var(--gold)] transition-colors">Privacy</Link>
        <Link href="/terms-of-service" onClick={onClose} className="text-[9px] tracking-[0.1em] uppercase text-[var(--slate-muted)] hover:text-[var(--gold)] transition-colors">Terms</Link>
        <Link href="/cookie-policy" onClick={onClose} className="text-[9px] tracking-[0.1em] uppercase text-[var(--slate-muted)] hover:text-[var(--gold)] transition-colors">Cookies</Link>
        <span suppressHydrationWarning className="text-[9px] text-[var(--slate-muted)] w-full mt-0.5">© {currentYear} The Private World of Luxury</span>
      </div>
    </div>
  );
}

function AppLayout({ children, currentPath }: AppLayoutProps) {
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const activePath = currentPath || pathname;
  const { user, isSubscribed, profile, signOut } = useAuth();
  const router = useRouter();
  const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
    setMounted(true);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    router.push('/');
  };

  const sidebarProps: SidebarContentProps = {
    activePath,
    currentYear,
    user,
    isSubscribed,
    profile,
    onClose: () => setMobileMenuOpen(false),
    onSignOut: handleSignOut,
  };

  return (
    <div suppressHydrationWarning className="min-h-screen bg-white flex overflow-x-hidden">
      {/* Desktop Sidebar */}
      <aside suppressHydrationWarning className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-[210px] bg-white border-r border-[var(--border)] z-40">
        <SidebarContent {...sidebarProps} />
      </aside>

      {/* Mobile Header */}
      <header suppressHydrationWarning className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[var(--border)] h-14 flex items-center justify-between px-4">
        <button onClick={() => setMobileMenuOpen(true)} className="p-1.5 text-[var(--slate-muted)]">
          <Menu size={20} />
        </button>
        <div suppressHydrationWarning className="text-center">
          <p className="text-[8px] tracking-[0.15em] uppercase text-[var(--slate-muted)] leading-none">THE PRIVATE WORLD OF</p>
          <p className="font-editorial text-lg font-bold text-[var(--gold)] leading-none">LUXURY</p>
        </div>
        <button className="p-1.5 text-[var(--slate-muted)]">
          <Search size={18} />
        </button>
      </header>

      {/* Mobile Drawer */}
      {mounted && mobileMenuOpen && (
        <div suppressHydrationWarning className="fixed inset-0 z-[100] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
          <div suppressHydrationWarning className="absolute left-0 top-0 bottom-0 w-[230px] bg-white shadow-xl flex flex-col">
            <div suppressHydrationWarning className="flex items-center justify-between px-4 h-14 border-b border-[var(--border)] flex-shrink-0">
              <span className="text-xs font-semibold tracking-widest uppercase text-[var(--slate-muted)]">Menu</span>
              <button onClick={() => setMobileMenuOpen(false)} className="text-[var(--slate-muted)]">
                <X size={18} />
              </button>
            </div>
            <div suppressHydrationWarning className="flex-1 min-h-0">
              <SidebarContent {...sidebarProps} />
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main suppressHydrationWarning className="flex-1 lg:ml-[210px] min-h-screen">
        {children}
      </main>
    </div>
  );
}

export default AppLayout;